import styled from "@emotion/styled";
import theme from "../../styles/theme";
import { Link } from "gatsby";
import { Button, Dropdown, Flow, Panel } from "@xometry/xometry_loft";

interface IBodyProps {
  isDarkMode: boolean;
  transitionPercent: number;
  isSearchOpen?: boolean;
}

interface IHeaderProps {
  isOpen: boolean;
}

interface ISearchProps {
  isOpen: boolean;
  showAnnouncementBanner: boolean;
  isDarkMode?: boolean;
}

export const HeaderBase = styled.header<IBodyProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 10;
  padding: ${theme.spacing[4]};
  transition: background-color 0.25s ease-out;
  background-color: rgba(
    255,
    255,
    255,
    ${(props) => (props.transitionPercent > 0.9 && props.isDarkMode ? 0 : 1)}
  );

  ${(props) =>
    props.isSearchOpen &&
    props.isDarkMode &&
    props.transitionPercent > 0.9 &&
    "background-color: rgb(20, 52, 77) !important;"}

  ${theme.largerThan.xl} {
    gap: ${theme.spacing[4]};
  }

  *,
  :after,
  :before {
    box-sizing: border-box;
  }
`;

export const HeaderUpperFlexbox = styled.div`
  max-width: ${theme.maxWidth};
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

export const HeaderLowerFlexbox = styled.div`
  max-width: ${theme.maxWidth};
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

export const SearchToggle = styled.span`
  cursor: pointer;

  &:hover {
    i {
      color: ${theme.colors.blues[100]};
    }
  }

  ${theme.smallerThan.xl} {
    display: none;
  }
`;

export const SearchCloseIcon = styled.i`
  cursor: pointer;
  font-size: ${theme.fontSizes[4]};
`;

export const Search = styled.div<ISearchProps>`
  position: fixed;
  z-index: 2;
  top: ${(props) => (props.showAnnouncementBanner ? "146px" : "62px")};
  left: 0;
  right: 0;
  transform: translateY(${(props) => (props.isOpen ? `0%` : `-120%`)});
  transition: transform 0.25s ease-out;

  ${theme.largerThan.md} {
    top: ${(props) => (props.showAnnouncementBanner ? "110px" : "62px")};
  }

  ${theme.largerThan.xl} {
    top: ${(props) => (props.showAnnouncementBanner ? "150px" : "102px")};
  }
`;

export const SearchBarBackground = styled.div<ISearchProps>`
  position: relative;
  z-index: 1;
  background-color: #fff;
  padding: ${theme.spacing[8]};
  transform: translateY(${(props) => (props.isOpen ? "0%" : "-100%")});
  transition: transform 0.5s ease-out;
  box-shadow: 0 5px 5px 0 rgba(0, 0, 0, 0.24);
`;

export const SearchOverlay = styled.div<ISearchProps>`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  height: 100%;
  background-color: rgba(
    ${(props) => (props.isDarkMode ? "20, 52, 77" : "255, 255, 255")},
    0.5
  );
  z-index: 1;
  transform: translateY(${(props) => (props.isOpen ? "0%" : "-120%")});
  animation: ${(props) => (props.isOpen ? "fade-in" : "fade-out")} 0.5s ease-out
    forwards;
  transition: transform 0.1s ease-out ${(props) => (props.isOpen ? "0" : "2s")};
  backdrop-filter: blur(${(props) => (props.isOpen ? `6px` : `0px`)});

  @keyframes fade-out {
    0% {
      opacity: 100%;
    }
    100% {
      opacity: 0%;
    }
  }
  @keyframes fade-in {
    0% {
      opacity: 0%;
    }
    100% {
      opacity: 100%;
    }
  }
`;

export const SearchBarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  max-width: ${theme.narrowWidth};
  margin: 0 auto;

  form {
    div {
      label {
        button {
          background-color: transparent;
          border: none;
          svg {
            color: ${theme.colors.darkBlues[100]} !important;
            height: 24px;
            width: 24px;
          }
        }
      }
    }
  }
`;

export const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  min-height: 30px;
`;

export const LogoLinkWrapper = styled(Link)<IBodyProps>`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 30px;
  overflow: hidden;
  position: relative;
  -webkit-transition: opacity 0.25s ease-out;
  -moz-transition: opacity 0.25s ease-out;
  transition: opacity 0.25s ease-out;

  .cls-1 {
    transition: fill 0.25s ease-out;
    fill: ${(props) =>
      props.isDarkMode && props.transitionPercent > 0.9 ? `#fff` : `#0E6CF1`};
  }
  .cls-2 {
    transition: fill 0.25s ease-out;
    fill: ${(props) =>
      props.isDarkMode && props.transitionPercent > 0.9 ? `#ccc` : `#0E6CF1`};
    stroke: ${(props) =>
      props.isDarkMode && props.transitionPercent > 0.9 ? `#ccc` : `#0E6CF1`};
    stroke-miterlimit: 10;
    stroke-width: 0.75px;
  }
  .cls-3 {
    transition: fill 0.25s ease-out;
    fill: ${(props) =>
      props.isDarkMode && props.transitionPercent > 0.9 ? `#ccc` : `#2437BF`};
  }

  .tagline,
  line {
    display: none;
  }

  .logo-desktop {
    position: absolute;
    top: 0;
    left: 0;
    height: 30px;
  }
`;

export const LoadingPanel = styled(Panel)`
  box-sizing: border-box;
  border: 1px solid ${theme.colors.grays[5]};
  border-radius: 4px;
  box-shadow: 0 1px 4px 0 rgba(37, 56, 66, 0.1);
  display: block;
  width: 100%;
  padding: ${theme.space[2]};
  margin-bottom: ${theme.space[2]};
`;

export const LoadingBlock = styled.div`
  height: 20px;
  display: block;
  background: ${theme.colors.darkBlues[10]};
`;

export const DropdownCustomContent = styled(Dropdown.Content)`
  min-width: 200px;
`;

export const DropdownCustomOption = styled(Dropdown.Option)`
  &:last-of-type {
    border-top: 1px solid ${theme.colors.darkBlues[10]};
  }
`;

export const CustomFlow = styled(Flow)`
  margin: 0px;
  width: 30px;
`;

export const UserNav = styled.div``;

export const HeaderLinksWrapper = styled.div<IHeaderProps>`
  flex-grow: 1;
  display: flex;

  ${theme.largerThan.xl} {
    gap: 1.5rem;
    align-items: start;
  }
`;

export const SearchQuickLinks = styled.div`
  max-width: ${theme.narrowWidth};
  margin: ${theme.spacing[8]} auto 0;
  display: flex;
  gap: ${theme.spacing[8]};

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin: ${theme.spacing[2]} 0;

      a {
        color: ${theme.colors.darkBlues[100]};
        text-decoration: none;

        &:hover {
          color: ${theme.colors.blues[100]};
        }
      }
    }
  }
`;

export const MobileLinks = styled.div`
  display: flex;

  ${theme.smallerThan.xl} {
    position: relative;
    z-index: 1;
  }

  ${theme.largerThan.xl} {
    order: initial;
    display: none;
    outline: none;
  }
`;

export const CustomButton = styled(Button)`
  width: 30px;
  height: 30px;
  &:focus:not(:active) {
    box-shadow: none;
  }
  background-color: transparent !important;
`;

export const AccountButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover {
    background-color: ${theme.colors.darkBlues[5]} !important;
    color: ${theme.colors.blues[100]};
  }
  &:focus:not(:active) {
    box-shadow: none;
    color: ${theme.colors.blues[100]};
  }
  &:active {
    color: ${theme.colors.blues[100]} !important;
  }
  background-color: transparent !important;
  color: ${theme.colors.blues[100]};
`;

export const LoginButton = styled(Button)<IBodyProps>`
  min-width: 80px;
  height: 30px;
  &:focus:not(:active) {
    box-shadow: none;
    color: ${theme.colors.blues[100]};
  }
  color: ${(props) =>
    props.isDarkMode && props.transitionPercent > 0.9
      ? `#fff`
      : theme.colors.darkBlues[100]};
  &:hover {
    background-color: ${theme.colors.darkBlues[5]} !important;
    color: ${theme.colors.blues[100]};
  }
  &:active {
    color: ${theme.colors.blues[100]} !important;
  }

  background-color: transparent !important;
  color: ${theme.colors.blues[100]};

  span {
    margin-right: ${theme.spacing[1]};
  }

  * {
    vertical-align: middle !important;
  }
`;

export const CustomIcon = styled.i<IBodyProps>`
  font-size: ${theme.fontSizes[2]} !important;
  height: 1.1em !important;
  color: ${(props) =>
    props.isDarkMode && props.transitionPercent > 0.9
      ? `#fff`
      : theme.colors.darkBlues[100]};
`;

export const MobileSearchButton = styled.div`
  ${theme.largerThan.xl} {
    display: none !important;
  }
`;
